import { useEffect, useRef } from 'react';

import { LEAD_IN_SEC, type CountTimerState } from '../useCountTimer';

// HI-3 slice 2b — the timer that SAYS what it is doing (interval-training.md
// §9.4). Three events, one hook, watching the engine both platforms already
// share:
//
//   - the 3-2-1 in the last three seconds of a phase (the most-cited feature of
//     every serious interval timer: it works with the phone in a pocket);
//   - a cue at the instant a phase BEGINS, different for work and for rest, so
//     an athlete who is not looking knows which one just started;
//   - and the instant it reaches zero.
//
// ⭐ The hook reports all three and judges none of them. A boundary between two
// phases raises TWO of them a tick apart (work ends, rest begins) and only the
// host knows which of the pair it already has a sound for — mobile has a
// scheduled bell at exactly that instant, web has nothing. Deciding here would
// mean encoding one host's notification stack in the shared tree.
//
// ⛔ Why this is NOT built on `timerNotification`, which §9.4 pointed at. That
// seam schedules a notifee trigger for a FUTURE instant, and the app asks for
// inexact alarms on purpose — AndroidManifest.xml says so in as many words:
// "enough for a ~10-30s window we tolerate around end-of-rest". A 3-2-1
// delivered ±10s is worse than no 3-2-1. These cues fire from the foreground
// tick instead, where the timing is the JS clock's and therefore exact, and the
// host decides how to make the noise. The scheduled end-of-phase cue is
// untouched and still rides notifee for the backgrounded case: the two answer
// different questions ([[feedback_reuse_nearest_sibling]]).
//
// And it is a SHARED hook rather than a mobile one because the lift is part of
// the work, not a follow-up (Antenore 2026-08-30): web-rnw mounts this same
// tree, so the web logger gets the cues from the same lines of code.

/** What just happened, in the only two flavours a cue has to distinguish. A
 *  hold and a prescribed bout are both WORK — the athlete is doing something;
 *  rest is the other thing. (The end-of-phase notification copy still says
 *  hold vs bout, because there the difference is what to do next.) */
export type TimerCuePhase = 'work' | 'rest';

export type TimerCueEvent =
  /** One of the last three seconds of the phase just began. */
  | { kind: 'leadIn'; phase: TimerCuePhase; secondsLeft: 1 | 2 | 3 }
  /** The phase itself just began. */
  | { kind: 'phaseStart'; phase: TimerCuePhase }
  /** The count reached zero. */
  | { kind: 'phaseEnd'; phase: TimerCuePhase };

// How many seconds before the end get a tick. Three is what every timer in the
// §7 recon does, and it is not arbitrary: it is the shortest lead that still
// lets you brace. The engine owns the number: in background it wakes on them.
const MAX_LEAD_IN = LEAD_IN_SEC;

function isLeadInSecond(sec: number): sec is 1 | 2 | 3 {
  return sec >= 1 && sec <= MAX_LEAD_IN && Number.isInteger(sec);
}

/** Fire the foreground cues for one running count. Does nothing at all when the
 *  host supplies no player (the seam is optional, so a host that cannot make a
 *  sound simply renders the same silent timer it has today) or when the count is
 *  not a count-down: a count-up has no last three seconds, and a stopwatch that
 *  ticked at 3-2-1 would be counting down something the athlete never set. */
export function usePhaseCues(
  state: CountTimerState,
  phase: TimerCuePhase,
  play: ((cue: TimerCueEvent) => void) | undefined,
): void {
  // The run this hook has already spoken for. `endsAt` is the natural identity
  // of a count-down: it changes on every start, and a +30s tap moves it, which
  // is exactly when the lead-in has to be allowed to fire again (the athlete
  // bought three more seconds of runway, so the 3-2-1 belongs to the new
  // deadline). Null while idle.
  const runRef = useRef<number | null>(null);
  // The last integer second already ticked inside THIS run. The same second
  // can render more than once (an action, a host re-render), so without this a
  // lead-in second could fire twice ([[feedback_one_tap_raises_one_prompt]],
  // the same guard useWorkBoutAutoComplete keeps, for the same reason).
  const spokenRef = useRef<number | null>(null);
  // Zero fires once per run, and `atZero` stays true for as long as the clock
  // sits on the deadline (the rest timer's grace window is three whole seconds
  // of it).
  const endedRef = useRef(false);

  // Keep the player in a ref: hosts rebuild the closure every render, and this
  // effect must key on the COUNT, never on the identity of the callback.
  const playRef = useRef(play);
  playRef.current = play;

  const { mode, endsAt, remainingSec, isPaused, atZero } = state;

  useEffect(() => {
    if (mode !== 'down' || endsAt === null) {
      runRef.current = null;
      spokenRef.current = null;
      endedRef.current = false;
      return;
    }
    if (runRef.current !== endsAt) {
      // ⚠️ A moved deadline is not always a new phase. `+30s` on a running rest
      // changes endsAt without the rest ever having ended, and re-announcing
      // "rest started" there would make the one gesture that BUYS time sound
      // like the start of the thing it is extending. The lead-in guard is
      // cleared either way: the athlete bought three fresh seconds of runway
      // and the 3-2-1 belongs to the new deadline.
      const wasIdle = runRef.current === null;
      runRef.current = endsAt;
      spokenRef.current = null;
      endedRef.current = false;
      if (wasIdle) {
        // A count that starts already inside its own lead-in (a prescribed 2s
        // bout) gets the phase cue and nothing else: three ticks crammed into
        // two seconds is noise, not a countdown.
        playRef.current?.({ kind: 'phaseStart', phase });
        if (remainingSec <= MAX_LEAD_IN) spokenRef.current = remainingSec;
      }
      return;
    }
    // A paused clock is not counting down, so it is not approaching anything.
    if (isPaused) return;
    if (atZero) {
      if (endedRef.current) return;
      endedRef.current = true;
      playRef.current?.({ kind: 'phaseEnd', phase });
      return;
    }
    if (!isLeadInSecond(remainingSec)) return;
    if (spokenRef.current !== null && spokenRef.current <= remainingSec) return;
    spokenRef.current = remainingSec;
    playRef.current?.({ kind: 'leadIn', phase, secondsLeft: remainingSec });
  }, [mode, endsAt, remainingSec, isPaused, atZero, phase]);
}
